/**
 * Staff — Training Fees Admin.
 *
 * Editable base fees per brand, VAT rate, multi-course discount tiers,
 * and City & Guilds retail/cost values. Persisted in the backend
 * `training_settings` singleton doc so changes flow into every downstream
 * booking form without a code push.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, PoundSterling, Save, Loader2, GraduationCap } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { BRAND } from '@/pages/trainingBrand';
import { formatApiError } from '@/utils/apiError';
import { invalidateTrainingSettings } from '@/hooks/useTrainingSettings';

const GREEN = BRAND.green;

const BRAND_ROWS = [
  { key: 'probound', label: 'ProBound',   hint: 'Resin-bound gravel training' },
  { key: 'proepoxy', label: 'ProEpoxy',   hint: 'Epoxy resin flooring training' },
  { key: 'prodecor', label: 'ProDecor / Walling', hint: 'Wall coatings training' },
];

function pctToDisplay(v) {
  return typeof v === 'number' ? String(Math.round(v * 10000) / 100) : '';
}

function displayToPct(raw) {
  const num = Number(raw);
  if (!Number.isFinite(num)) return null;
  return Math.round((num / 100) * 10000) / 10000;
}

export default function TrainingFeesAdmin() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    probound: '', proepoxy: '', prodecor: '',
    vat_rate: '', discount_2: '', discount_3: '',
    cg_retail: '', cg_cost: '',
  });

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager'].includes(user.role)) {
      navigate('/staff/training-courses', { replace: true });
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/staff/training-settings`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to load settings');
        const s = await res.json();
        if (!mounted) return;
        setForm({
          probound: String(s.brand_fees_net?.probound ?? ''),
          proepoxy: String(s.brand_fees_net?.proepoxy ?? ''),
          prodecor: String(s.brand_fees_net?.prodecor ?? ''),
          vat_rate: pctToDisplay(s.vat_rate),
          discount_2: pctToDisplay(s.discount_2_courses),
          discount_3: pctToDisplay(s.discount_3_plus_courses),
          cg_retail: String(s.city_guilds_retail_net ?? ''),
          cg_cost: String(s.city_guilds_cost_net ?? ''),
          venue_address: s.venue_address || '',
          pack_probound: s.pack_urls?.probound || '',
          pack_proepoxy: s.pack_urls?.proepoxy || '',
          pack_prodecor: s.pack_urls?.prodecor || '',
        });
      } catch (e) {
        toast.error(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user, navigate]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        brand_fees_net: {
          probound: Number(form.probound),
          proepoxy: Number(form.proepoxy),
          prodecor: Number(form.prodecor),
        },
        vat_rate: displayToPct(form.vat_rate),
        discount_2_courses: displayToPct(form.discount_2),
        discount_3_plus_courses: displayToPct(form.discount_3),
        city_guilds_retail_net: Number(form.cg_retail),
        city_guilds_cost_net: Number(form.cg_cost),
        venue_address: form.venue_address.trim(),
        pack_urls: { probound: form.pack_probound.trim(), proepoxy: form.pack_proepoxy.trim(), prodecor: form.pack_prodecor.trim() },
      };
      const res = await fetch(`${API_URL}/api/staff/training-settings`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to save');
      }
      invalidateTrainingSettings();
      toast.success('Fees updated — new bookings will use these numbers');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const vatRate = displayToPct(form.vat_rate) ?? 0.20;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${GREEN.tint} 0%, #FFFFFF 300px)` }}>
      <div className="border-b sticky top-0 z-20 shadow-sm" style={{ background: GREEN.primary, color: GREEN.ink }}>
        <div className="max-w-3xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Link to="/staff/training-courses" className="text-white/80 hover:text-white" data-testid="training-fees-back" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Link to="/" className="text-white/80 hover:text-white hidden sm:inline-flex" aria-label="Home">
            <Home className="w-5 h-5" />
          </Link>
          <div className="w-8 h-8 rounded-lg bg-white/15 text-white flex items-center justify-center">
            <PoundSterling className="w-4 h-4" />
          </div>
          <h1 className="text-base sm:text-lg font-bold text-white truncate flex-1">Training Fees</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <div className="text-center py-20 text-gray-500">Loading…</div>
        ) : (
          <>
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <GraduationCap className="w-5 h-5 text-gray-500" />
                <h2 className="text-base font-bold text-gray-900">Base course fees (net, before VAT)</h2>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Enter the ex-VAT price for one seat on each course. VAT is added on top when trainees see the gross figure.
              </p>
              <div className="space-y-3">
                {BRAND_ROWS.map((b) => {
                  const net = Number(form[b.key]) || 0;
                  const gross = Math.round(net * (1 + vatRate) * 100) / 100;
                  return (
                    <div key={b.key} className="grid grid-cols-3 gap-3 items-start">
                      <div>
                        <Label className="text-sm">{b.label}</Label>
                        <p className="text-[10px] text-gray-500">{b.hint}</p>
                      </div>
                      <div>
                        <Input
                          type="number" min="0" step="0.01"
                          value={form[b.key]}
                          onChange={(e) => setForm(f => ({ ...f, [b.key]: e.target.value }))}
                          className="bg-white"
                          data-testid={`fee-${b.key}`}
                        />
                      </div>
                      <div className="text-sm text-gray-600 pt-2">
                        = <strong className="text-gray-900">£{gross.toFixed(2)}</strong> gross
                      </div>
                    </div>
                  );
                })}
                <div className="grid grid-cols-3 gap-3 items-center border-t pt-3">
                  <Label className="text-sm">VAT rate (%)</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={form.vat_rate}
                    onChange={(e) => setForm(f => ({ ...f, vat_rate: e.target.value }))}
                    className="bg-white"
                    data-testid="fee-vat"
                  />
                  <span className="text-xs text-gray-500">UK standard rate is 20</span>
                </div>
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="text-base font-bold text-gray-900 mb-1">Multi-course discount tiers</h2>
              <p className="text-xs text-gray-500 mb-4">
                Optional at checkout — staff will tick a box to apply. Full price is still encouraged.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>2 courses (%)</Label>
                  <Input
                    type="number" min="0" step="0.5"
                    value={form.discount_2}
                    onChange={(e) => setForm(f => ({ ...f, discount_2: e.target.value }))}
                    className="bg-white"
                    data-testid="fee-discount-2"
                  />
                </div>
                <div>
                  <Label>3+ courses (%)</Label>
                  <Input
                    type="number" min="0" step="0.5"
                    value={form.discount_3}
                    onChange={(e) => setForm(f => ({ ...f, discount_3: e.target.value }))}
                    className="bg-white"
                    data-testid="fee-discount-3"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="text-base font-bold text-gray-900 mb-1">City &amp; Guilds certificate</h2>
              <p className="text-xs text-gray-500 mb-4">
                Recorded on the booking when given free as an incentive. Used in the revenue report to show what's been given away.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Retail price (net £)</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={form.cg_retail}
                    onChange={(e) => setForm(f => ({ ...f, cg_retail: e.target.value }))}
                    className="bg-white"
                    data-testid="fee-cg-retail"
                  />
                </div>
                <div>
                  <Label>Our cost (net £)</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={form.cg_cost}
                    onChange={(e) => setForm(f => ({ ...f, cg_cost: e.target.value }))}
                    className="bg-white"
                    data-testid="fee-cg-cost"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="text-base font-bold text-gray-900 mb-1">Training venue</h2>
              <p className="text-xs text-gray-500 mb-4">
                Quoted in the confirmation text every trainee receives the moment they're booked (course, date, venue, balance owed). One venue for all courses.
              </p>
              <Label htmlFor="venue-address">Venue address</Label>
              <Input
                id="venue-address"
                value={form.venue_address}
                onChange={(e) => setForm(f => ({ ...f, venue_address: e.target.value }))}
                placeholder="Full address of the training venue, including postcode"
                maxLength={300}
                className="bg-white"
                data-testid="fee-venue-address"
              />
            </Card>

            <Card className="p-5">
              <h2 className="text-base font-bold text-gray-900 mb-1">Course info packs</h2>
              <p className="text-xs text-gray-500 mb-4">
                Links (PDF / Google Drive / web page) to each course's information pack. Linked from the automatic enquiry welcome email — leave blank until you have them.
              </p>
              <div className="space-y-3">
                {[['pack_probound', 'ProBound pack link'], ['pack_proepoxy', 'ProEpoxy pack link'], ['pack_prodecor', 'ProDecor pack link']].map(([k, l]) => (
                  <div key={k}>
                    <Label htmlFor={k}>{l}</Label>
                    <Input id={k} type="url" value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder="https://…" className="bg-white" data-testid={`fee-${k.replace('_', '-')}`} />
                  </div>
                ))}
              </div>
            </Card>

            <div className="flex justify-end">
              <Button
                onClick={save}
                disabled={saving}
                className="text-white font-bold"
                style={{ background: GREEN.primary }}
                data-testid="fees-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
